import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../../config";
import { Loader2, Lock, Bot, Sparkles, CheckCircle2, AlertCircle } from "lucide-react";

/**
 * CitationTierPanel
 *
 * Free vs Pro AI Citation visualizer. Drop on snapshot results page
 * (or dashboard). Calls POST /api/ai-citations/scan with the domain.
 *
 * Free response shows:
 *  - total count (HUGE number — the upsell anchor)
 *  - ChatGPT card with the count visible
 *  - Claude / Gemini / Perplexity cards LOCKED with a blur + Pro CTA
 *
 * Pro response shows:
 *  - full breakdown including queries + snippets per engine
 *
 * Auth handled by axios — the API uses the existing cookie-based JWT.
 */

const ENGINE_ICONS = {
  chatgpt: "🟢",
  claude: "🟣",
  gemini: "🔵",
  perplexity: "🟠",
};

export default function CitationTierPanel({ defaultDomain = "", onUpgradeClick }) {
  const [domain, setDomain] = useState(defaultDomain);
  const [topic, setTopic] = useState("");
  const [brandKw, setBrandKw] = useState("");
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (defaultDomain && !domain) setDomain(defaultDomain);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultDomain]);

  const runScan = useCallback(async () => {
    if (!domain) {
      setError("Zadaj doménu.");
      return;
    }
    setScanning(true);
    setError("");
    setResult(null);
    try {
      const r = await axios.post(
        `${API}/ai-citations/scan`,
        {
          domain,
          topic: topic || undefined,
          brand_keywords: brandKw
            ? brandKw.split(",").map((s) => s.trim()).filter(Boolean)
            : [],
        },
        { withCredentials: true },
      );
      setResult(r.data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 401) {
        setError("Pre AI Citation scan musíš byť prihlásený.");
      } else if (e?.response?.status === 429) {
        setError("Denný limit citation scanov dosiahnutý. Skús zajtra.");
      } else {
        setError(detail || "Skenovanie zlyhalo.");
      }
    }
    setScanning(false);
  }, [domain, topic, brandKw]);

  return (
    <section data-testid="citation-tier-panel" className="bg-white rounded-2xl border border-gray-200 p-6 lg:p-8">
      <header className="mb-5">
        <h3 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Bot className="w-6 h-6 text-purple-600" />
          AI Citation Attribution
        </h3>
        <p className="text-sm text-gray-500 mt-1 max-w-2xl">
          Zisti, ktoré AI vyhľadávače (ChatGPT, Claude, Gemini, Perplexity) tvoj web už citujú,
          pri akých otázkach a ako často. Pro tier odomkne presné prompty + snippety.
        </p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <input
          type="text"
          value={domain}
          onChange={(e) => setDomain(e.target.value)}
          placeholder="tvojadomena.sk"
          data-testid="citation-domain-input"
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-gray-900 font-mono"
        />
        <input
          type="text"
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          placeholder="Téma (napr. e-shop SEO)"
          data-testid="citation-topic-input"
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-gray-900"
        />
        <input
          type="text"
          value={brandKw}
          onChange={(e) => setBrandKw(e.target.value)}
          placeholder="Brand keywords (čiarka)"
          data-testid="citation-brand-input"
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-gray-900"
        />
      </div>

      <button
        type="button"
        onClick={runScan}
        disabled={scanning || !domain}
        data-testid="citation-scan-btn"
        className="w-full md:w-auto px-6 py-3 bg-gray-900 text-white rounded-lg text-sm font-bold hover:bg-black disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
      >
        {scanning ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Skenujem citácie v 4 AI engine...
          </>
        ) : (
          <>
            <Sparkles className="w-4 h-4" />
            Spustiť Citation scan
          </>
        )}
      </button>

      {error && (
        <div
          data-testid="citation-error"
          className="mt-3 flex items-start gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700"
        >
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {result && (
        <div data-testid="citation-result" className="mt-6 space-y-5">
          {/* Total citations hero */}
          <div className="bg-gradient-to-br from-purple-50 to-blue-50 border border-purple-100 rounded-xl p-6 text-center">
            <p className="text-xs font-bold text-purple-700 uppercase tracking-widest mb-2">
              AI Citácie zachytené
            </p>
            <p
              data-testid="citation-total"
              className="text-6xl font-black text-gray-900 tabular-nums"
            >
              {result.total_citations}
            </p>
            <p className="text-sm text-gray-500 mt-2">
              naprieč 4 AI engine pre <span className="font-mono font-bold">{result.domain}</span>
            </p>
          </div>

          {/* Engine grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {(result.engine_results || []).map((e) => (
              <EngineCard
                key={e.engine_id}
                engine={e}
                isPro={result.is_pro}
                onUpgradeClick={onUpgradeClick}
              />
            ))}
          </div>

          {/* Pro CTA */}
          {result.pro_locked && (
            <div
              data-testid="citation-pro-cta"
              className="bg-gradient-to-r from-gray-900 to-gray-800 text-white rounded-xl p-5 lg:p-6 flex items-start gap-4"
            >
              <Lock className="w-6 h-6 text-amber-400 flex-shrink-0 mt-1" />
              <div className="flex-1">
                <h4 className="text-lg font-bold mb-1">Odomkni plný Citation report</h4>
                <p className="text-sm text-gray-300 mb-3">
                  {result.pro_unlock_message}
                </p>
                <button
                  type="button"
                  onClick={onUpgradeClick}
                  data-testid="citation-upgrade-btn"
                  className="px-5 py-2.5 bg-amber-400 text-gray-900 rounded-lg text-sm font-bold hover:bg-amber-300 transition-colors"
                >
                  Upgrade na Pro
                </button>
              </div>
            </div>
          )}

          {/* Queries (Pro only) */}
          {result.is_pro && result.queries?.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <h4 className="text-sm font-bold text-gray-900 mb-3">
                Buyer-intent queries ({result.queries.length})
              </h4>
              <ul className="space-y-1.5">
                {result.queries.map((q, i) => (
                  <li
                    key={i}
                    data-testid={`citation-query-${i}`}
                    className="text-sm text-gray-700 flex items-start gap-2"
                  >
                    <CheckCircle2 className="w-4 h-4 text-purple-500 flex-shrink-0 mt-0.5" />
                    <span className="font-mono">{q}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function EngineCard({ engine, isPro, onUpgradeClick }) {
  const isLocked = engine.status === "locked";
  const isUnavailable = engine.status === "unavailable";
  const count = engine.citation_count;

  return (
    <div
      data-testid={`citation-engine-${engine.engine_id}`}
      className={`relative p-4 rounded-xl border ${
        isLocked
          ? "bg-gray-50 border-gray-200"
          : isUnavailable
            ? "bg-amber-50 border-amber-200"
            : count > 0
              ? "bg-green-50 border-green-200"
              : "bg-white border-gray-200"
      }`}
    >
      <div className="flex items-center gap-2 mb-2">
        <span className="text-lg">{ENGINE_ICONS[engine.engine_id] || "⚪️"}</span>
        <span className="text-xs font-bold uppercase tracking-wider text-gray-700">
          {engine.engine_label}
        </span>
      </div>

      {isLocked ? (
        <div>
          <div className="text-3xl font-black text-gray-300 blur-sm select-none">??</div>
          <button
            type="button"
            onClick={onUpgradeClick}
            data-testid={`citation-engine-${engine.engine_id}-unlock`}
            className="absolute inset-0 flex items-center justify-center bg-white/60 backdrop-blur-[1px] rounded-xl text-xs font-bold text-gray-900 hover:bg-white/80"
          >
            <Lock className="w-3.5 h-3.5 mr-1" />
            Odomkni v Pro
          </button>
        </div>
      ) : isUnavailable ? (
        <div>
          <div className="text-sm font-semibold text-amber-700">Coming soon</div>
          <p className="text-[11px] text-amber-600 mt-1">Vyžaduje API kľúč.</p>
        </div>
      ) : (
        <div>
          <div
            data-testid={`citation-engine-${engine.engine_id}-count`}
            className="text-3xl font-black text-gray-900 tabular-nums"
          >
            {count}
          </div>
          <p className="text-[11px] text-gray-500 mt-1">
            {count === 0 ? "Žiadne citácie" : count === 1 ? "citácia" : "citácií"}
            {isPro && engine.citations?.length > 0 && (
              <span> · {engine.citations.length} snippets</span>
            )}
          </p>
        </div>
      )}

      {/* Pro snippets */}
      {isPro && !isLocked && !isUnavailable && engine.citations?.length > 0 && (
        <div className="mt-3 pt-3 border-t border-green-200 space-y-1.5">
          {engine.citations.slice(0, 3).map((c, i) => (
            <div
              key={i}
              data-testid={`citation-snippet-${engine.engine_id}-${i}`}
              className="text-[11px] text-gray-700 italic line-clamp-2"
            >
              “{c.snippet}”
              <div className="text-[10px] not-italic text-gray-400 mt-0.5">
                @ <span className="font-mono">{c.query}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
